/**
 * Page context — what the route the question was asked from is about.
 *
 * `localAnswer()` has always been handed the pathname and ignored it, so
 * "give me the full history" on `/machines/T-09` answered for no machine at
 * all. This reads the route into entities: a machine, a mine, an alert, an
 * incident, a workflow module, a generated report, and the chip view key.
 *
 * The router uses these **only** for a deictic question ("this machine",
 * "this report", "it") or when the intent it picked needs an entity the
 * question did not name — and the answer says so, e.g. "· T-09 (this page)".
 * A page never overrides what the user typed.
 *
 * Engine layer — pure.
 */

import { BY_ID } from '../../data/core.ts';
import type { MineCode } from '../../schema/machine.ts';
import type { ReportRef } from '../../schema/report.ts';
import { isMineCode, reportRefFromPath } from '../report-catalog.ts';
import { ALERTS } from '../seed/alerts.ts';
import { INCIDENTS } from '../seed/incidents.ts';
import { MODULE_IDS, type ModuleId } from '../seed/modules.ts';
import { machineFromPath, viewForPath, type AssistantView } from './suggestions.ts';

/** What the current route is about. Every field is optional. */
export interface PageContext {
  readonly pathname: string;
  readonly view: AssistantView;
  readonly machine?: string;
  readonly mine?: MineCode;
  readonly alert?: string;
  readonly incident?: string;
  readonly module?: ModuleId;
  readonly report?: ReportRef;
}

/** Read a pathname (with or without a query string) into a {@link PageContext}. */
export function pageContext(pathname: string): PageContext {
  const [path, query = ''] = pathname.split('?');
  const view = viewForPath(path);
  const out: {
    -readonly [K in keyof PageContext]: PageContext[K];
  } = { pathname, view };

  const machine = machineFromPath(path);
  if (machine && BY_ID[machine]) {
    out.machine = machine;
    out.mine = BY_ID[machine].mine;
  }

  const alert = /^\/issues\/alerts\/(AL-\d{4})/i.exec(path)?.[1]?.toUpperCase();
  const a = alert ? ALERTS.find((x) => x.id === alert) : undefined;
  if (a) {
    out.alert = a.id;
    out.machine = a.machine;
    out.mine = BY_ID[a.machine]?.mine;
  }

  const incident = /^\/issues\/incidents\/(IR-\d{4}-\d{3})/i.exec(path)?.[1]?.toUpperCase();
  const inc = incident ? INCIDENTS.find((x) => x.id === incident) : undefined;
  if (inc) {
    out.incident = inc.id;
    out.machine = inc.machine;
    out.mine = inc.mine as MineCode;
  }

  const mod = /^\/workflows\/modules\/([^/]+)/.exec(path)?.[1];
  if (mod && (MODULE_IDS as readonly string[]).includes(mod)) out.module = mod as ModuleId;

  const site = /^\/sites\/([A-Za-z]{2})\b/.exec(path)?.[1]?.toUpperCase();
  if (isMineCode(site)) out.mine = site;
  const siteParam = /(?:^|&)site=([A-Za-z]{2})\b/.exec(query)?.[1]?.toUpperCase();
  if (isMineCode(siteParam)) out.mine = siteParam;

  const report = reportRefFromPath(path);
  if (report) {
    out.report = siteParam && report.kind === 'sos' && isMineCode(siteParam)
      ? { kind: 'sos', mine: siteParam }
      : report;
    if (report.mine) out.mine = report.mine;
  }

  return out;
}

/** How an answer names an entity that came from the page. */
export const PAGE_NOTE = '(this page)';
/** How an answer names an entity that came from the conversation. */
export const MEMORY_NOTE = '(from our conversation)';
